/** @jsx h */
import { h, Fragment } from 'preact'
import { useEffect, useState } from 'preact/compat'
import Button from './button'
import Textarea from './textarea'
import Step from './step'
import { useLabel } from '../use-label'
import useAnalytics from '../use-analytics'
import { getBoutiqueServices } from '../api'
import { useGlobal } from '../use-global'
import Input from './input'
import { CONTEXT_POV, EXTERNAL_POINT_OF_SALE } from '../constants'
import InputCheckboxRadio from './input-checkbox-radio'
import Select from './select'
import clsx from 'clsx'
import Spin from './icons/spin'

const CardStepVisit = ({ isOpen, isDone, onToggle, isEnabled, isHighlighted, selectorClassName }) => {
  const t = useLabel()
  const analytics = useAnalytics()
  const [
    {
      selectedBoutique: { id, boutiqueType },
      selectedPurpose,
      selectedCountry,
      canvasConfig,
      maisonConfig
    },
    dispatch
  ] = useGlobal()
  const extPovInitialText =
    canvasConfig.context === 'productdiscovery' ? `${canvasConfig.context}: ${canvasConfig.product.reference}` : ''
  const [services, setServices] = useState(null)
  const [externalPurposeOfVisit, setExternalPurposeOfVisit] = useState(extPovInitialText)
  const [serialNumber, setSerialNumber] = useState('')
  const [mainService, setMainService] = useState(selectedPurpose.type)
  const [subService, setSubService] = useState(selectedPurpose.value)
  const [povLength, setPovLength] = useState()
  const [pickUpType, setPickUpType] = useState('Standard')
  const [isVisible, setIsVisible] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const selectedSubService = services?.find(s => s.type === mainService)?.values?.find(sms => sms.value === subService)
  const isExternalPointOfSale = boutiqueType === EXTERNAL_POINT_OF_SALE
  const isFastAppointmentsEnabled = maisonConfig.fastAppointmentsEnabled
  const displayProduct =
    canvasConfig.context === 'productdiscovery' && (canvasConfig.product.image || canvasConfig.product.name)
  const showSerialNumber =
    selectedCountry === 'ES' &&
    canvasConfig.maisonId === 'CAR' &&
    selectedSubService?.assetType === 'RICBA_Service' &&
    selectedSubService?.allowSerialNum

  let context = CONTEXT_POV[canvasConfig.context]

  // If the maison has disabled the fastAppointments then we should ignore context in case it is clickNcollect or pickUp
  if (!isFastAppointmentsEnabled && (canvasConfig.context === 'clickncollect' || canvasConfig.context === 'pickup')) {
    context = null
  }

  const onSelectMainServiceHandler = service => {
    setMainService(service.type)

    // If there is only one pov, select it
    setSubService(service.values.length === 1 ? service.values[0].value : null)

    // If there is only one pov, hide the dropdown.
    // Except if the context has the value of 'productdiscovery', then we want to show the dropdown
    setIsVisible(service.values.length !== 1 || canvasConfig.context === 'productdiscovery')
  }

  useEffect(() => {
    if (Object.keys(selectedPurpose).length === 0) {
      setMainService(null)
      setSubService(null)
      services?.length > 0 && onSelectMainServiceHandler(services[0])
    }
  }, [selectedPurpose])

  useEffect(() => {
    if (id && !isExternalPointOfSale) {
      setIsLoading(true)
      setServices([])
      getBoutiqueServices({
        apiBaseUrl: canvasConfig.apiBaseUrl,
        boutiqueId: id,
        language: canvasConfig.locale
      })
        .then(data => data.sort((a, b) => (a.type >= b.type ? 1 : -1)))
        .then(data => {
          return data
            .map(service => {
              return {
                ...service,
                values: service.values.filter(v => (context ? v[context] : v.isWebEnabled))
              }
            })
            .filter(service => service.values.length > 0)
        })
        .then(data => {
          const numOfPov = data.reduce((acc, cur) => acc + cur.values.length, 0)
          setServices(data)
          setPovLength(numOfPov)

          if (data.length === 0) {
            console.error('No POV were found')
            return
          }

          onSelectMainServiceHandler(data[0])
        })
        .catch(console.error)
        .finally(() => setIsLoading(false))
    }
  }, [id, boutiqueType, canvasConfig, isExternalPointOfSale])

  useEffect(() => {
    if (
      !services?.length ||
      !mainService ||
      !subService ||
      showSerialNumber ||
      ['pickup', 'clickncollect', 'productdiscovery'].some(ctx => canvasConfig.context.includes(ctx))
    ) {
      return
    }

    if (povLength === 1) {
      onSubmitHandler()
      dispatch({ type: 'set-step-disabled', payload: 1 })
    }
  }, [services, mainService, subService, povLength, canvasConfig, showSerialNumber])

  const onSubmitHandler = () => {
    const selectedMainService = services?.find(s => s.type === mainService)
    const selectedSubService = selectedMainService?.values.find(sms => sms.value === subService)

    if (isExternalPointOfSale) {
      dispatch({
        type: 'set-purpose-message',
        payload: externalPurposeOfVisit
      })
    }

    if (!isExternalPointOfSale && showSerialNumber) {
      dispatch({
        type: 'set-serial-number',
        payload: serialNumber
      })
    }

    // in case the user change the purpose after inserting the serial number
    if (!showSerialNumber && serialNumber) {
      dispatch({
        type: 'set-serial-number',
        payload: null
      })
    }

    dispatch({ type: 'go-to-2' })
    dispatch({
      type: 'select-purpose',
      payload: {
        title: selectedMainService?.title,
        type: selectedMainService?.type,
        assetType: selectedSubService?.assetType,
        value: selectedSubService?.value,
        label: selectedSubService?.label,
        allowSerialNum: selectedSubService?.allowSerialNum,
        enabledForVirtual: selectedSubService?.enabledForVirtual,
        pickUpType
      }
    })
    canvasConfig.onSectionExpanded({
      selectorClassName: 'rcc_c_card-step-details',
      index: 2
    })
    analytics.trackStep('baa_purpose')
  }

  return (
    <Step
      isOpen={isOpen}
      isDone={isDone}
      isEnabled={isEnabled}
      isHighlighted={isHighlighted}
      onToggle={() => {
        isEnabled && onToggle(1)
      }}
      className={selectorClassName}
      index={1}
      title={t('Purpose_of_Your_Visit.PYV_Purpose_Of_Your_Visit')}
      summary={
        selectedPurpose.type
          ? `${selectedPurpose.title || ''} - ${selectedPurpose.label || ''}`
          : externalPurposeOfVisit
      }
    >
      {isLoading && (
        <div className='rcc-flex rcc-justify-center rcc-items-center rcc-py-6'>
          <Spin />
        </div>
      )}

      {!isLoading && !isExternalPointOfSale && services?.length === 0 && (
        <h5>Sorry, this purpose of visit is not available in the selected boutique. Please select another boutique.</h5>
      )}

      {!isLoading && services?.length > 0 && !isExternalPointOfSale && (
        <>
          <fieldset>
            <legend
              className={clsx('rcc_c_content-title rcc-m-0', {
                'rcc-text-right': canvasConfig.direction === 'rtl'
              })}
            >
              {canvasConfig.locale === 'ar'
                ? `* ${t('Purpose_of_Your_Visit.PYV_Intro_Text')}`
                : `${t('Purpose_of_Your_Visit.PYV_Intro_Text')} *`}
            </legend>
            <div
              className={clsx(
                'rcc-flex rcc-gap-x-9 rcc-gap-y-2.5 rcc-mt-5 rcc-mb-4 mobile:rcc-flex-col mobile:rcc-items-start',
                { 'rcc-flex-row-reverse': canvasConfig.direction === 'rtl' }
              )}
            >
              {services.map(service => (
                <InputCheckboxRadio
                  key={service.type}
                  checked={mainService === service.type}
                  value={service.type}
                  onChange={() => onSelectMainServiceHandler(service)}
                  label={service.title}
                  type='radio'
                  id={`rcc__${service.type}`}
                />
              ))}
            </div>
          </fieldset>

          {isVisible && mainService && (
            <Select
              options={services.find(service => service.type === mainService)?.values}
              value={subService}
              onChange={e => setSubService(e.target.value)}
              ariaLabel='Select purpose of visit'
              disabled={services.find(service => service.type === mainService)?.values?.length === 1}
              placeholder={
                canvasConfig.locale === 'ar'
                  ? mainService === 'products'
                    ? t('Purpose_of_Your_Visit.PYV_Pick_Collection_Placeholder')
                    : t('Purpose_of_Your_Visit.PYV_Pick_Service_Placeholder')
                  : ''
              }
            />
          )}
        </>
      )}

      {/* Standard/Fast appointment radio checkbox - only if the maison have fastAppointment enabled */}
      {!isLoading &&
        services?.length > 0 &&
        subService &&
        isFastAppointmentsEnabled &&
        (selectedSubService?.isFastPickup || selectedSubService?.isClickNCollect) && (
          <div
            className={clsx(
              'rcc-flex rcc-gap-x-9 rcc-gap-y-2.5 rcc-mt-5 rcc-mb-4 mobile:rcc-flex-col mobile:rcc-items-start',
              { 'rcc-flex-row-reverse': canvasConfig.direction === 'rtl' }
            )}
          >
            <InputCheckboxRadio
              checked={pickUpType === 'Standard'}
              value='Standard'
              label={t('Purpose_of_Your_Visit.PYV_Standard_Appointment')}
              description={t('Purpose_of_Your_Visit.PYV_Standard_Appointment_Description')}
              type='radio'
              id={`rcc__standard_appointment`}
              onChange={() => setPickUpType('Standard')}
            />
            <InputCheckboxRadio
              checked={pickUpType === 'Fast'}
              value='Fast'
              label={t('Purpose_of_Your_Visit.PYV_Fast_Appointment')}
              description={t('Purpose_of_Your_Visit.PYV_Fast_Appointment_Description')}
              type='radio'
              id={`rcc__fast_appointment`}
              onChange={() => setPickUpType('Fast')}
            />
          </div>
        )}

      {!isLoading && services?.length > 0 && showSerialNumber && (
        <Input
          className='rcc-mt-5'
          label={t('Purpose_of_Your_Visit.PYV_SerialNumber') + ' *'}
          value={serialNumber}
          onChange={e => setSerialNumber(e.target.value.trim())}
        />
      )}

      {!isLoading && displayProduct && (
        <div className='rcc-mt-4 rcc-flex rcc-gap-4'>
          <img className='rcc-w-24 rcc-h-24 rcc-object-cover' src={canvasConfig.product.image} alt='Product image' />
          <div className='rcc-flex rcc-flex-col rcc-justify-between'>
            <div>
              <p className='rcc-font-bold'>{canvasConfig.product.name}</p>
              <small>{canvasConfig.product.reference}</small>
            </div>
            <p>{t('Purpose_of_Your_Visit.PYV_Product_Disclaimer')}</p>
          </div>
        </div>
      )}

      {!isLoading && isExternalPointOfSale && (
        <Textarea
          className='rcc-w-full rcc-mt-0'
          placeholder={t('Purpose_of_Your_Visit.Ret_Intro_Text')}
          value={externalPurposeOfVisit}
          onChange={e => {
            setExternalPurposeOfVisit(e.target.value)
          }}
        />
      )}

      {!isLoading && (
        <Button
          disabled={
            (isExternalPointOfSale && !externalPurposeOfVisit) ||
            (!isExternalPointOfSale && !mainService) ||
            (!isExternalPointOfSale && !subService) ||
            (showSerialNumber && serialNumber.length === 0)
          }
          className='rcc_c_content-confirm rcc-mt-5'
          onClick={onSubmitHandler}
        >
          {t('Global.Confirm')}
        </Button>
      )}
    </Step>
  )
}

export default CardStepVisit
